import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUserEventsStore } from '../store/user-events-map-store';
import { usePointModal } from '../PointModal/usePointModal';

type LatLng = {
  lat: number;
  lng: number;
};

export type RandomPoint = {
  lat: number;
  lng: number;
  device_id: string;
};

const generateSectorCoordinates = (
  center: LatLng,
  radius?: number,
  angle?: number,
  rotation?: number,
): LatLng[] => {
  const coordinates: LatLng[] = [];
  const step = 10;

  // Validar se os parâmetros são números válidos
  if (
    !center ||
    isNaN(center.lat) ||
    isNaN(center.lng) ||
    !radius ||
    !angle ||
    !rotation
  ) {
    return [];
  }

  coordinates.push(center);

  for (let i = -angle / 2; i <= angle / 2; i += step) {
    const rad = ((i + rotation) * Math.PI) / 180; // Aplicar rotação e converter para radianos

    const lat = center.lat + (radius / 111320) * Math.cos(rad); // Aproximação de 111.32km/° para latitude
    const lng =
      center.lng +
      (radius / (111320 * Math.cos((center.lat * Math.PI) / 180))) *
        Math.sin(rad);

    if (isNaN(lat) || isNaN(lng)) {
      continue;
    }

    coordinates.push({ lat, lng });
  }

  coordinates.push(center);

  return coordinates;
};

const generateRandomPoints = (
  center: LatLng,
  radius: number,
  count: number,
): LatLng[] => {
  const points: RandomPoint[] = [];

  for (let i = 0; i < count; i++) {
    const angle = Math.random() * 2 * Math.PI; // Ângulo aleatório
    const distance = Math.random() * radius; // Distância aleatória dentro do raio

    const lat = center.lat + (distance / 111320) * Math.cos(angle);
    const lng =
      center.lng +
      (distance / (111320 * Math.cos((center.lat * Math.PI) / 180))) *
        Math.sin(angle);

    const device_id = Math.floor(Math.random() * 1000).toString();

    points.push({ lat, lng, device_id });
  }

  return points;
};

export const useMarker = () => {
  const { state: userEventsState, actions: userEventActions } =
    useUserEventsStore();
  const [radius, setRadius] = useState(20); // Raio inicial pequeno
  const [opacity, setOpacity] = useState(1); // Opacidade inicial
  const [rotation, setRotation] = useState(0); // Começa de cima (270°)
  const [randomPoints, setRandomPoints] = useState<RandomPoint[]>([]);
  const { events } = usePointModal();

  useEffect(() => {
    const interval = setInterval(() => {
      // Diminui a opacidade
      setRotation((prev) => (prev + 0.5) % 360); // Gira 5 graus a cada ciclo
    }, 10);

    return () => clearInterval(interval);
  }, []);

  const markers = {
    customSvgMarker: {
      url:
        'data:image/svg+xml;utf-8,' +
        encodeURIComponent(`<?xml version="1.0" encoding="UTF-8"?>
  <svg width="16px" height="20px" viewBox="0 0 16 20" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
      <title>MapPinArea</title>
      <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
          <g id="MapPinArea" fill="#00DCFF" fill-rule="nonzero">
              <g id="User" transform="translate(4, 5)">
                  <path d="M7.95463593,7.22484526 C7.36944639,6.21314393 6.46760836,5.48768966 5.41517708,5.14377515 C5.93575201,4.83385862 6.34022351,4.36163707 6.56646687,3.79962518 C6.7926795,3.23758255 6.82815299,2.61682731 6.66744642,2.032686 C6.50673985,1.44853853 6.15870624,0.933296927 5.67683246,0.566087957 C5.19495868,0.198875913 4.60586521,0 4.00001867,0 C3.39414139,0 2.80504793,0.198875913 2.32317415,0.566087957 C1.84128499,0.933296927 1.49326367,1.44853853 1.33255403,2.032686 C1.17184132,2.61682731 1.20732403,3.23758255 1.43354895,3.79962518 C1.65977695,4.36163707 2.0642423,4.83385862 2.58482952,5.14377515 C1.53238287,5.48729004 0.63056021,6.21274432 0.0453553035,7.22484526 C0.0238959971,7.25982691 0.00966049388,7.29877396 0.00349105857,7.33935022 C-0.00267837674,7.37995722 -0.000655711853,7.42136344 0.00943916885,7.46114047 C0.0195309756,7.5009175 0.0374921169,7.53829683 0.0622590029,7.5710345 C0.087025889,7.60377216 0.118100539,7.63119187 0.153644724,7.65172591 C0.189191984,7.67225996 0.228489473,7.68547798 0.269219427,7.69058075 C0.309949381,7.69568352 0.351291053,7.69254809 0.390800646,7.68142036 C0.430310238,7.67029262 0.467188522,7.65135704 0.499259134,7.62572022 C0.531329746,7.60011415 0.557947155,7.56832941 0.577534421,7.53224114 C1.30145172,6.2811399 2.58098707,5.53416791 4.00001867,5.53416791 C5.41901953,5.53416791 6.69855488,6.2811399 7.42247218,7.53224114 C7.4420533,7.56832941 7.46867378,7.60011415 7.50073517,7.62572022 C7.5328273,7.65135704 7.56968407,7.67029262 7.60921518,7.68142036 C7.64871555,7.69254809 7.6900603,7.69568352 7.73079025,7.69058075 C7.7715202,7.68547798 7.8108054,7.67225996 7.8463711,7.65172591 C7.88190606,7.63119187 7.91298379,7.60377216 7.9377599,7.5710345 C7.96250526,7.53829683 7.98048792,7.5009175 7.99057051,7.46114047 C8.00065309,7.42136344 8.00268191,7.37995722 7.99650325,7.33935022 C7.99035533,7.29877396 7.9761229,7.25982691 7.95463593,7.22484526 Z M1.84823214,2.76760499 C1.84823214,2.3420154 1.97443045,1.92600733 2.21088243,1.57214856 C2.4473006,1.21829287 2.78337652,0.942494212 3.17656659,0.779632801 C3.56972592,0.616768316 4.00238562,0.574157099 4.41979849,0.657184726 C4.83721135,0.740209279 5.22059549,0.945147039 5.52153606,1.24607838 C5.82247663,1.54700665 6.02741746,1.93041539 6.11041435,2.34782518 C6.19344197,2.76520731 6.1508369,3.19786701 5.98797857,3.59105708 C5.82512023,3.98424715 5.54932465,4.32029232 5.19545051,4.55674123 C4.84160711,4.79319015 4.42557753,4.91937615 4.00001867,4.91937615 C3.42949192,4.91876136 2.88254243,4.69187246 2.47914681,4.28847685 C2.07574198,3.8850505 1.84884386,3.33810101 1.84823214,2.76760499 Z" id="Shape"></path>
              </g>
              <path d="M0,8 C0,5.87827 0.84285,3.84344 2.3431,2.34315 C3.8434,0.84285 5.8783,0 8,0 C10.1217,0 12.1566,0.84285 13.6569,2.34315 C15.1571,3.84344 16,5.87827 16,8 C16,15.4937 8.8025,19.6925 8.5,19.8687 C8.3489,19.9551 8.1778,20.0006 8.0037,20.0006 C7.8297,20.0006 7.6586,19.9551 7.5075,19.8687 C7.1975,19.6925 0,15.5 0,8 Z M2,8 C2,13.275 6.48,16.7763 8,17.8125 C9.5188,16.7775 14,13.275 14,8 C14,6.4087 13.3679,4.88258 12.2426,3.75736 C11.1174,2.63214 9.5913,2 8,2 C6.4087,2 4.8826,2.63214 3.7574,3.75736 C2.6321,4.88258 2,6.4087 2,8 Z" id="Shape"></path>
          </g>
      </g>
  </svg>`),
      // Size of the marker on the map
      scaledSize: new google.maps.Size(25, 25.25),
      // The origin point of the marker (0, 0 is the top-left corner)
      origin: new google.maps.Point(0, 0),
      // The anchor point of the marker (where the marker is attached to the map)
      // For a pin-shaped marker, this is typically at the bottom center
      anchor: new google.maps.Point(13, 24),
    },
  };

  function getMarkerIcon() {
    return markers.customSvgMarker;
  }

  const deviceSelected = userEventsState.deviceSelected;

  async function onMarkerClick() {
    userEventActions.setDeviceSelected(deviceSelected);

    userEventActions.setDeviceModalIsOpen(true);
    userEventActions.setPointModalIsOpen(false);
  }

  async function onRandomPointClick(point: RandomPoint) {
    // userEventActions.setPointSelected({
    //   device_id: point.device_id,
    //   lat: point.lat,
    //   lng: point.lng,
    // });
    // userEventActions.setDeviceModalIsOpen(false);
    // userEventActions.setPointModalIsOpen(true);
  }

  const markerIcon = useMemo(() => {
    return getMarkerIcon();
  }, [userEventsState.deviceModalIsOpen, userEventsState.deviceSelected]);

  const markerPosition: LatLng = {
    lat: Number(userEventsState.pointSelected?.event_info.loc_lat),
    lng: Number(userEventsState.pointSelected?.event_info.loc_lon),
  };

  const generateSectorCoordinatesMemoized = useCallback(() => {
    return generateSectorCoordinates(markerPosition, radius, 100, rotation);
  }, [markerPosition, radius, rotation]);

  const generateSectorCoordinatesMinDistanceMemoized = useCallback(() => {
    return generateSectorCoordinates(markerPosition, radius, 360, 360);
  }, [markerPosition, radius]);

  const sectorCoordinates = useMemo(
    () => generateSectorCoordinatesMemoized(),
    [generateSectorCoordinatesMemoized],
  );

  const sectorCoordinatesMinDistance = useMemo(
    () => generateSectorCoordinatesMinDistanceMemoized(),
    [generateSectorCoordinatesMinDistanceMemoized],
  );

  const randomPointsFunction = useCallback(() => {
    return generateRandomPoints(
      markerPosition,
      radius,
      events?.apps_data.length || 0,
    ) as RandomPoint[];
  }, [markerPosition, radius]);

  useEffect(() => {
    setRandomPoints(randomPointsFunction || []);
  }, [userEventsState.eventListModalIsOpen]);

  return {
    markerIcon,
    markerPosition,
    userEventsState,
    userEventActions,
    opacity,
    sectorCoordinates,
    sectorCoordinatesMinDistance,
    randomPoints,
    onMarkerClick,
    onRandomPointClick,
  };
};
